import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BudgetTracker = ({ budgetData, spendingInsights, recommendations }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [showRecommendations, setShowRecommendations] = useState(true);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    })?.format(amount);
  };

  const getSpendingPercentage = (spent, budget) => {
    return Math.min((spent / budget) * 100, 100);
  };

  const getSpendingColor = (percentage) => {
    if (percentage >= 90) return 'var(--color-error)';
    if (percentage >= 75) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'food': 'Utensils',
      'beverages': 'Coffee',
      'snacks': 'Cookie',
      'meals': 'ChefHat',
      'desserts': 'IceCream'
    };
    return icons?.[category] || 'ShoppingBag';
  };

  const periods = [
    { value: 'week', label: 'Esta Semana' },
    { value: 'month', label: 'Este Mes' },
    { value: 'semester', label: 'Este Semestre' }
  ];

  const currentData = budgetData?.[selectedPeriod];

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
            <Icon name="PieChart" size={20} color="var(--color-success)" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg text-foreground">
              Seguimiento de Presupuesto
            </h3>
            <p className="text-sm text-muted-foreground">
              Gestiona tus gastos en comida rescatada
            </p>
          </div>
        </div>
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e?.target?.value)}
          className="px-3 py-1.5 text-sm border border-border rounded-md bg-background text-foreground"
        >
          {periods?.map(period => (
            <option key={period?.value} value={period?.value}>
              {period?.label}
            </option>
          ))}
        </select>
      </div>
      {/* Budget Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Target" size={18} color="var(--color-primary)" />
            <span className="text-sm font-medium text-foreground">Presupuesto</span>
          </div>
          <p className="font-data font-bold text-xl text-foreground">
            {formatCurrency(currentData?.budget)}
          </p>
          <p className="text-xs text-muted-foreground">
            Límite establecido
          </p>
        </div>

        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="TrendingDown" size={18} color="var(--color-error)" />
            <span className="text-sm font-medium text-foreground">Gastado</span>
          </div>
          <p className="font-data font-bold text-xl text-error">
            {formatCurrency(currentData?.spent)}
          </p>
          <p className="text-xs text-muted-foreground">
            {getSpendingPercentage(currentData?.spent, currentData?.budget)?.toFixed(1)}% del presupuesto
          </p>
        </div>

        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Wallet" size={18} color="var(--color-success)" />
            <span className="text-sm font-medium text-foreground">Disponible</span>
          </div>
          <p className="font-data font-bold text-xl text-success">
            {formatCurrency(currentData?.remaining)}
          </p>
          <p className="text-xs text-muted-foreground">
            Restante para gastar
          </p>
        </div>
      </div>
      {/* Budget Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Progreso del Presupuesto</span>
          <span className="text-sm text-muted-foreground">
            {getSpendingPercentage(currentData?.spent, currentData?.budget)?.toFixed(1)}%
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-3">
          <div 
            className="h-3 rounded-full transition-all duration-500"
            style={{ 
              width: `${getSpendingPercentage(currentData?.spent, currentData?.budget)}%`,
              backgroundColor: getSpendingColor(getSpendingPercentage(currentData?.spent, currentData?.budget))
            }}
          />
        </div>
        <div className="flex justify-between text-xs text-muted-foreground mt-1">
          <span>{formatCurrency(0)}</span>
          <span>{formatCurrency(currentData?.budget)}</span>
        </div>
      </div>
      {/* Category Breakdown */}
      <div className="mb-6">
        <h4 className="font-medium text-foreground mb-3">Gastos por Categoría</h4>
        <div className="space-y-3">
          {currentData?.categories?.map((category) => (
            <div key={category?.name} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                  <Icon 
                    name={getCategoryIcon(category?.id)} 
                    size={16} 
                    color="var(--color-primary)" 
                  />
                </div>
                <div>
                  <p className="font-medium text-foreground text-sm">{category?.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {category?.transactions} transacciones
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-medium text-foreground">
                  {formatCurrency(category?.amount)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {((category?.amount / currentData?.spent) * 100)?.toFixed(1)}%
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Spending Insights */}
      <div className="mb-6">
        <h4 className="font-medium text-foreground mb-3">Análisis de Gastos</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-muted/30 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="TrendingUp" size={16} color="var(--color-accent)" />
              <span className="text-sm font-medium text-foreground">Promedio Diario</span>
            </div>
            <p className="font-data font-bold text-lg text-foreground">
              {formatCurrency(spendingInsights?.dailyAverage)}
            </p>
            <p className="text-xs text-muted-foreground">
              {spendingInsights?.dailyTrend > 0 ? '+' : ''}{spendingInsights?.dailyTrend?.toFixed(1)}% vs período anterior
            </p>
          </div>

          <div className="bg-muted/30 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Clock" size={16} color="var(--color-warning)" />
              <span className="text-sm font-medium text-foreground">Hora Favorita</span>
            </div>
            <p className="font-data font-bold text-lg text-foreground">
              {spendingInsights?.favoriteTime}
            </p>
            <p className="text-xs text-muted-foreground">
              {spendingInsights?.favoriteTimePercentage}% de tus compras
            </p>
          </div>
        </div>
      </div>
      {/* Recommendations */}
      {showRecommendations && recommendations?.length > 0 && (
        <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-foreground flex items-center space-x-2">
              <Icon name="Lightbulb" size={16} color="var(--color-accent)" />
              <span>Recomendaciones</span>
            </h4>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowRecommendations(false)}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
          
          <div className="space-y-2">
            {recommendations?.map((rec, index) => (
              <div key={index} className="flex items-start space-x-2">
                <Icon name="ArrowRight" size={14} color="var(--color-accent)" className="mt-0.5" />
                <p className="text-sm text-foreground">{rec?.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Action Buttons */}
      <div className="flex items-center space-x-2 mt-6">
        <Button variant="outline" size="sm" className="flex-1">
          <Icon name="Settings" size={16} className="mr-2" />
          Ajustar Presupuesto
        </Button>
        <Button variant="outline" size="sm" className="flex-1">
          <Icon name="Download" size={16} className="mr-2" />
          Exportar Reporte
        </Button>
      </div>
    </div>
  );
};

export default BudgetTracker;